import React from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { keyframes, styled } from '@mui/system';
import type { ProgressMessage, ProgressStepState } from '../hooks/useStageStatus';
import type { StepProgressStep } from '../constants/stepProgress';

// Horizontal shimmer sweep used to highlight the currently-running step,
// echoing Claude Code's CLI activity indicator (e.g. "Pondering...").
const stepShimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

// Apply shimmer via a styled component rather than the `sx` prop because
// MUI's `sx` pipeline silently drops vendor-prefixed properties like
// `WebkitBackgroundClip` and `WebkitTextFillColor`, leaving bold without
// the gradient sweep. `styled()` passes the CSS through emotion directly.
const StepRow = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'state',
})<{ state: ProgressStepState; component?: React.ElementType }>(({ theme, state }) => {
  // Tolerate missing theme so the component still renders in unthemed
  // unit-test contexts. In production the ThemeProvider always supplies one.
  const palette = (theme as { palette?: any } | undefined)?.palette;
  const textPrimary = palette?.text?.primary ?? 'inherit';
  const textSecondary = palette?.text?.secondary ?? 'inherit';
  const errorMain = palette?.error?.main ?? 'inherit';
  // Shimmer glint: a bright band swept across the text-colored gradient. It
  // must read as a light flash relative to the page, so it tracks the mode's
  // base surface (cream in light, warm-charcoal in dark) — a hardcoded white
  // glint would vanish on the dark text in dark mode.
  const shimmerGlint = palette?.background?.default ?? 'transparent';
  const base = {
    paddingTop: '4px',
    paddingBottom: '4px',
    listStyle: 'none' as const,
  };
  switch (state) {
    case 'running':
      // We intentionally ignore `prefers-reduced-motion` here — the shimmer
      // is the primary visual cue that a step is running, and falling back
      // to plain bold is too easy to miss against the surrounding pending
      // rows. Users with reduced-motion enabled will still see the sweep.
      return {
        ...base,
        fontWeight: 700,
        color: textPrimary,
        background: `linear-gradient(90deg, currentColor 0%, currentColor 40%, ${shimmerGlint} 50%, currentColor 60%, currentColor 100%)`,
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        animation: `${stepShimmer} 1.8s linear infinite`,
      };
    case 'done':
      return { ...base, textDecoration: 'line-through', color: textSecondary };
    case 'failed':
      return { ...base, color: errorMain };
    case 'pending':
    default:
      return { ...base, color: textSecondary };
  }
});

interface StepProgressSpinnerProps {
  /**
   * Canonical ordered list of steps to render. The full skeleton is always
   * shown — steps without a matching message render as `pending`.
   */
  steps: StepProgressStep[];
  /**
   * Current progress messages from the backend. Looked up by `step_id` to
   * determine each step's state.
   */
  messages: ProgressMessage[];
  size?: number;
}

const StepProgressSpinner: React.FC<StepProgressSpinnerProps> = ({ steps, messages, size = 60 }) => {
  // Build a lookup of step_id → latest message so we can resolve each step's
  // state. If multiple messages share a step_id, the last one wins (matches
  // the backend's "update in place" semantics for save_progress).
  const messageByStepId = new Map<string, ProgressMessage>();
  for (const m of messages) {
    if (m.step_id) {
      messageByStepId.set(m.step_id, m);
    }
  }

  return (
    <Stack direction="row" spacing={3} alignItems="center" justifyContent="center" sx={{ py: 4 }}>
      <CircularProgress size={size} />
      {steps.length > 0 && (() => {
        // If the backend hasn't explicitly marked any step as running (e.g.
        // between click and the first emit, or between one step finishing
        // and the next emitting), infer the running step as the first
        // non-done, non-failed step. Guarantees the spinner always
        // highlights *something* while a run is in flight.
        const hasExplicitRunning = steps.some(
          (s) => messageByStepId.get(s.step_id)?.state === 'running',
        );
        const inferredRunningIdx = hasExplicitRunning
          ? -1
          : steps.findIndex((s) => {
              const st = messageByStepId.get(s.step_id)?.state;
              return st !== 'done' && st !== 'failed';
            });
        const inferredRunningId =
          inferredRunningIdx >= 0 ? steps[inferredRunningIdx].step_id : null;

        return (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, textAlign: 'left' }}>
          {steps.map((step) => {
            const msg = messageByStepId.get(step.step_id);
            const rawState = msg?.state;
            const state: ProgressStepState =
              step.step_id === inferredRunningId
                ? 'running'
                : (rawState ?? 'pending');
            const isRunning = state === 'running';
            return (
              <StepRow
                component="li"
                variant="body1"
                key={step.step_id}
                state={state}
                data-testid="step-progress-item"
                data-state={state}
                data-running={isRunning ? 'shimmer' : undefined}
              >
                {step.label}
              </StepRow>
            );
          })}
        </Box>
        );
      })()}
    </Stack>
  );
};

export default StepProgressSpinner;
